import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  User, UserPlus, Search, Loader2, Building, Phone, Mail, CheckCircle, ArrowLeft,
  Route, Truck, Package, FileText, Zap, X, Save, DollarSign, Weight
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import TransportForm from './TransportForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pays CEDEAO avec indicatifs téléphoniques
const countries = [
  { code: 'CI', name: "Côte d'Ivoire", phoneCode: '+225' },
  { code: 'SN', name: 'Sénégal', phoneCode: '+221' },
  { code: 'ML', name: 'Mali', phoneCode: '+223' },
  { code: 'BF', name: 'Burkina Faso', phoneCode: '+226' },
  { code: 'GH', name: 'Ghana', phoneCode: '+233' },
  { code: 'TG', name: 'Togo', phoneCode: '+228' },
  { code: 'BJ', name: 'Bénin', phoneCode: '+229' },
  { code: 'NE', name: 'Niger', phoneCode: '+227' },
  { code: 'NG', name: 'Nigeria', phoneCode: '+234' },
  { code: 'GN', name: 'Guinée', phoneCode: '+224' },
  { code: 'GW', name: 'Guinée-Bissau', phoneCode: '+245' },
  { code: 'GM', name: 'Gambie', phoneCode: '+220' },
  { code: 'CV', name: 'Cap-Vert', phoneCode: '+238' },
  { code: 'SL', name: 'Sierra Leone', phoneCode: '+232' },
  { code: 'LR', name: 'Liberia', phoneCode: '+231' },
];

// Helper pour obtenir l'indicatif d'un pays
const getPhoneCode = (countryCode) => {
  const country = countries.find(c => c.code === countryCode);
  return country?.phoneCode || '+225';
};

const AdminCreateOperationModal = ({ isOpen, onClose, onSuccess }) => {
  // État pour le choix du client
  const [step, setStep] = useState('client'); // 'client' ou 'operation'
  const [clientTab, setClientTab] = useState('existing');
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  
  // Modal création d'opération
  const [showCreateOperationModal, setShowCreateOperationModal] = useState(false);
  
  // État pour le nouveau client - avec indicatif par défaut CI (+225)
  const [newClient, setNewClient] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '+225 ',
    accountType: 'particulier',     // 'particulier' | 'entreprise',
    companyName: '',
    rccm: '',                        // Registre de Commerce (entreprise),
    compteContribuable: '',          // NCC/IFU (entreprise),
    sector: '',                      // Secteur d'activité (entreprise),
    address: '',                     // Adresse postale,
    country: 'CI'
  });

  // Templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showSaveTemplatePrompt, setShowSaveTemplatePrompt] = useState(false);
  const [lastCreatedOperation, setLastCreatedOperation] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await axios.get(`${API}/templates?type=operation`, {});
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen, fetchTemplates]);

  // Charger et rechercher les clients
  const fetchClients = useCallback(async (search = '') => {
    setLoadingClients(true);
    try {
      // Utilise l'endpoint spécifique pour la recherche de clients
      // accessible aux rôles privilégiés (admin, operateur, commercial, etc.)
      const response = await axios.get(`${API}/unified-users/clients/search`, {
        params: { 
          q: search,
          limit: 50
        }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
      if (error.response?.status === 403) {
        toast({
          variant: "destructive",
          title: "Accès refusé",
          description: "Vous n'avez pas les droits pour rechercher des clients."
        });
      }
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && step === 'client') {
      fetchClients(clientSearch);
    }
  }, [isOpen, step, clientSearch, fetchClients]);

  // Recherche de client (par nom, téléphone, email, matricule)
  const filteredClients = clients.filter(client => {
    if (!clientSearch) return true;
    const search = clientSearch.toLowerCase().trim();
    return (
      client.firstName?.toLowerCase().includes(search) ||
      client.lastName?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.phone?.includes(search) ||
      client.companyName?.toLowerCase().includes(search) ||
      client.clientNumber?.toLowerCase().includes(search) ||
      client.id?.toLowerCase().includes(search)
    );
  });

  const handleClientSelect = (client) => {
    setSelectedClient(client);
  };

  const handleNewClientChange = (field, value) => {
    if (field === 'country') {
      // Quand le pays change, mettre à jour l'indicatif téléphonique
      const newPhoneCode = getPhoneCode(value);
      const currentPhone = newClient.phone;
      
      // Si le téléphone est vide ou ne contient que l'ancien indicatif, le remplacer
      const oldPhoneCode = getPhoneCode(newClient.country);
      let updatedPhone = newPhoneCode;
      
      if (currentPhone && currentPhone !== oldPhoneCode) {
        // Retirer l'ancien indicatif s'il est présent au début
        if (currentPhone.startsWith(oldPhoneCode)) {
          updatedPhone = newPhoneCode + currentPhone.slice(oldPhoneCode.length);
        } else if (currentPhone.startsWith('+')) {
          // Si autre indicatif, garder le numéro sans indicatif
          const phoneWithoutCode = currentPhone.replace(/^\+\d{1,4}\s*/, '');
          updatedPhone = newPhoneCode + (phoneWithoutCode ? ' ' + phoneWithoutCode : '');
        } else {
          // Numéro sans indicatif, ajouter le nouvel indicatif
          updatedPhone = newPhoneCode + ' ' + currentPhone;
        }
      }
      
      setNewClient(prev => ({ 
        ...prev, 
        country: value,
        phone: updatedPhone
      }));
    } else {
      setNewClient(prev => ({ ...prev, [field]: value }));
    }
  };

  const validateNewClient = () => {
    if (!newClient.firstName || !newClient.lastName || !newClient.email || !newClient.phone) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newClient.email)) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer un email valide",
        variant: "destructive"
      });
      return false;
    }
    if (newClient.accountType === 'entreprise' && !newClient.companyName?.trim()) {
      toast({
        title: "Erreur",
        description: "La raison sociale est obligatoire pour un compte entreprise",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };

  // Créer un nouveau client et passer à l'étape opération (sans créer l'op tout de suite)
  // L'op sera créée après que l'utilisateur ait rempli le TransportForm (étape 2)
  const handleCreateNewClient = async () => {
    if (!validateNewClient()) return;

    setCreatingClient(true);
    try {
      // Générer un mot de passe temporaire aléatoire
      const tempPassword = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-4).toUpperCase() + '!';

      const payload = {
        firstName: newClient.firstName,
        lastName: newClient.lastName,
        email: newClient.email,
        phone: newClient.phone,
        country: newClient.country?.toLowerCase() || 'ci',
        password: tempPassword,
        role: 'client',
        userType: 'client',
        accountType: newClient.accountType === 'entreprise' ? 'company' : 'individual',
        companyName: newClient.accountType === 'entreprise' ? newClient.companyName : undefined,
        rccm: newClient.rccm || undefined,
        compteContribuable: newClient.compteContribuable || undefined,
        sector: newClient.sector || undefined,
        address: newClient.address || undefined,
        sendWelcomeEmail: true,
      };

      const response = await axios.post(`${API}/unified-users`, payload, {});

      // Le backend renvoie { success, message, user: {...} } — l'id est dans `user`.
      const createdUser = response.data?.user || response.data || {};
      if (!createdUser.id) {
        toast({
          title: "Client créé mais non identifié",
          description: "Impossible de récupérer l'identifiant du nouveau client. Sélectionnez-le dans l'onglet « Client existant » avant de créer l'opération.",
          variant: "destructive",
        });
        setClientTab('existing');
        setClientSearch(newClient.email);
        return;
      }

      setSelectedClient({
        ...newClient,
        ...createdUser,
        id: createdUser.id,
        companyName: createdUser.companyName || newClient.companyName || '',
      });

      toast({
        title: "Client créé",
        description: newClient.accountType === 'entreprise'
          ? `Le compte entreprise « ${newClient.companyName} » a été créé. Mot de passe temporaire à transmettre.`
          : `Le compte de ${newClient.firstName} ${newClient.lastName} a été créé. Email d'activation envoyé.`,
      });

      setStep('operation');
    } catch (error) {
      console.error('Erreur création client:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer le client",
        variant: "destructive"
      });
    } finally {
      setCreatingClient(false);
    }
  };

  // Passer à l'étape opération avec un client existant
  const handleContinueWithExistingClient = () => {
    if (!selectedClient) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un client",
        variant: "destructive"
      });
      return;
    }
    setStep('operation');
  };

  // Callback quand l'opération est créée via TransportForm
  const handleOperationCreated = (operation) => {
    setLastCreatedOperation(operation);
    
    toast({
      title: "Opération créée",
      description: `L'opération ${operation.operationNumber} a été créée pour ${selectedClient.firstName} ${selectedClient.lastName}`,
    });
    
    // Show save-as-template prompt
    setShowSaveTemplatePrompt(true);
  };

  // Save operation as template
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast({ title: "Erreur", description: "Le nom du template est requis", variant: "destructive" });
      return;
    }
    setSavingTemplate(true);
    try {
      const op = lastCreatedOperation;
      await axios.post(`${API}/templates`, {
        name: templateName.trim(),
        description: `Créé depuis l'opération ${op?.operationNumber || ''}`,
        type: 'operation',
        departureAddress: op?.departure || op?.departureAddress || '',
        arrivalAddress: op?.arrival || op?.arrivalAddress || '',
        vehicleType: op?.vehicleType || '',
        operationType: op?.operationType || '',
        merchandiseType: op?.merchandiseType || '',
        containerType: op?.containerType || '',
        weight: op?.weight || 1,
        numberOfTrucks: op?.numberOfTrucks || 1,
        notes: op?.specialInstructions || op?.notes || '',
      }, {});
      toast({ title: "Template sauvegardé", description: `Le template "${templateName}" a été créé` });
      setShowSaveTemplatePrompt(false);
      onSuccess && onSuccess(lastCreatedOperation);
      handleClose();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible de sauvegarder le template", variant: "destructive" });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSkipSaveTemplate = () => {
    setShowSaveTemplatePrompt(false);
    onSuccess && onSuccess(lastCreatedOperation);
    handleClose();
  };

  const handleClose = () => {
    setStep('client');
    setClientTab('existing');
    setSelectedClient(null);
    setClientSearch('');
    setSelectedTemplate(null);
    setShowSaveTemplatePrompt(false);
    setLastCreatedOperation(null);
    setTemplateName('');
    setNewClient({
      firstName: '',
      lastName: '',
      email: '',
      phone: '+225 ',
      accountType: 'particulier',
      companyName: '',
      rccm: '',
      compteContribuable: '',
      sector: '',
      address: '',
      country: 'CI'
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'operation' && (
              <Button variant="ghost" size="sm" onClick={() => setStep('client')} className="mr-2 p-1">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            Créer une opération pour un client
          </DialogTitle>
          <DialogDescription>
            {step === 'client' 
              ? "Sélectionnez un client existant ou créez-en un nouveau" 
              : `Opération pour ${selectedClient?.firstName} ${selectedClient?.lastName}`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Étape 1: Sélection du client */}
        {step === 'client' && (
          <div className="space-y-4">
            <Tabs value={clientTab} onValueChange={setClientTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="existing" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Client existant
                </TabsTrigger>
                <TabsTrigger value="new" className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Nouveau client
                </TabsTrigger>
              </TabsList>

              <TabsContent value="existing" className="space-y-4">
                {/* Recherche multi-critères */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher par nom, téléphone, email ou N° client..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Liste des clients */}
                <div className="max-h-[350px] overflow-y-auto space-y-2">
                  {loadingClients ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#1db584]" />
                    </div>
                  ) : filteredClients.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {clientSearch ? "Aucun client trouvé pour cette recherche" : "Aucun client enregistré"}
                    </div>
                  ) : (
                    filteredClients.map((client) => (
                      <Card
                        key={client.id}
                        className={`cursor-pointer transition-all ${
                          selectedClient?.id === client.id
                            ? 'ring-2 ring-[#1db584] bg-green-50'
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleClientSelect(client)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-[#1db584]/10 flex items-center justify-center">
                                <User className="w-5 h-5 text-[#1db584]" />
                              </div>
                              <div>
                                <p className="font-medium">{client.firstName} {client.lastName}</p>
                                <div className="flex items-center gap-3 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" /> {client.email}
                                  </span>
                                  {client.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" /> {client.phone}
                                    </span>
                                  )}
                                </div>
                                {client.companyName && (
                                  <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                                    <Building className="w-3 h-3" /> {client.companyName}
                                  </p>
                                )}
                              </div>
                            </div>
                            {selectedClient?.id === client.id && (
                              <CheckCircle className="w-5 h-5 text-[#1db584]" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={handleClose}>Annuler</Button>
                  <Button 
                    onClick={handleContinueWithExistingClient} 
                    className="bg-[#1db584] hover:bg-[#18a075]"
                    disabled={!selectedClient}
                  >
                    Continuer
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="new" className="space-y-4">
                {/* Type de compte (particulier / entreprise) */}
                <div className="bg-zinc-50 rounded-lg p-3 border border-zinc-200">
                  <Label className="text-sm font-semibold text-zinc-700 mb-2 block">Type de compte</Label>
                  <div className="flex gap-2" data-testid="account-type-toggle">
                    {[
                      { id: 'particulier', label: 'Particulier', icon: User },
                      { id: 'entreprise', label: 'Entreprise', icon: Building },
                    ].map(opt => {
                      const Icon = opt.icon;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => handleNewClientChange('accountType', opt.id)}
                          data-testid={`account-type-${opt.id}`}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium border transition ${
                            newClient.accountType === opt.id
                              ? 'bg-[#1db584] text-white border-[#1db584]'
                              : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Prénom *</Label>
                    <Input
                      value={newClient.firstName}
                      onChange={(e) => handleNewClientChange('firstName', e.target.value)}
                      placeholder="Prénom"
                      data-testid="new-client-firstName"
                    />
                  </div>
                  <div>
                    <Label>Nom *</Label>
                    <Input
                      value={newClient.lastName}
                      onChange={(e) => handleNewClientChange('lastName', e.target.value)}
                      placeholder="Nom"
                      data-testid="new-client-lastName"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => handleNewClientChange('email', e.target.value)}
                      placeholder="email@exemple.com"
                      data-testid="new-client-email"
                    />
                  </div>
                  <div>
                    <Label>Pays</Label>
                    <Select value={newClient.country} onValueChange={(v) => handleNewClientChange('country', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name} ({c.phoneCode})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Téléphone *</Label>
                    <div className="flex gap-2">
                      <div className="w-24 flex items-center justify-center bg-gray-100 border rounded-md text-sm font-medium text-gray-700">
                        {getPhoneCode(newClient.country)}
                      </div>
                      <Input
                        value={newClient.phone.replace(getPhoneCode(newClient.country), '').trim()}
                        onChange={(e) => {
                          const phoneNumber = e.target.value.replace(/[^\d\s]/g, '');
                          handleNewClientChange('phone', getPhoneCode(newClient.country) + ' ' + phoneNumber);
                        }}
                        placeholder="XX XX XX XX XX"
                        className="flex-1"
                        data-testid="new-client-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Adresse (optionnel)</Label>
                    <Input
                      value={newClient.address}
                      onChange={(e) => handleNewClientChange('address', e.target.value)}
                      placeholder="Quartier, ville"
                      data-testid="new-client-address"
                    />
                  </div>
                </div>

                {/* Champs entreprise — visibles uniquement si accountType=entreprise */}
                {newClient.accountType === 'entreprise' && (
                  <div className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-4 space-y-3" data-testid="enterprise-fields">
                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                      <Building className="w-4 h-4" />
                      Informations entreprise
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Raison sociale *</Label>
                        <Input
                          value={newClient.companyName}
                          onChange={(e) => handleNewClientChange('companyName', e.target.value)}
                          placeholder="Ex: Diallo Logistics SARL"
                          data-testid="new-client-companyName"
                        />
                      </div>
                      <div>
                        <Label>RCCM (optionnel)</Label>
                        <Input
                          value={newClient.rccm}
                          onChange={(e) => handleNewClientChange('rccm', e.target.value)}
                          placeholder="Ex: CI-ABJ-2024-B-12345"
                          data-testid="new-client-rccm"
                        />
                        <p className="text-[10px] text-zinc-500 mt-1">Registre de Commerce et du Crédit Mobilier</p>
                      </div>
                      <div>
                        <Label>N° Compte Contribuable (optionnel)</Label>
                        <Input
                          value={newClient.compteContribuable}
                          onChange={(e) => handleNewClientChange('compteContribuable', e.target.value)}
                          placeholder="Ex: 1245678N"
                          data-testid="new-client-ncc"
                        />
                        <p className="text-[10px] text-zinc-500 mt-1">NCC / IFU pour facturation OHADA</p>
                      </div>
                      <div className="col-span-2">
                        <Label>Secteur d'activité (optionnel)</Label>
                        <Select value={newClient.sector || ''} onValueChange={(v) => handleNewClientChange('sector', v)}>
                          <SelectTrigger data-testid="new-client-sector">
                            <SelectValue placeholder="Sélectionner un secteur" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="import_export">Import / Export</SelectItem>
                            <SelectItem value="agriculture">Agriculture / Agroalimentaire</SelectItem>
                            <SelectItem value="btp">BTP / Construction</SelectItem>
                            <SelectItem value="commerce">Commerce de gros / détail</SelectItem>
                            <SelectItem value="industrie">Industrie / Manufacturing</SelectItem>
                            <SelectItem value="petrole">Pétrole / Mines</SelectItem>
                            <SelectItem value="distribution">Distribution / FMCG</SelectItem>
                            <SelectItem value="services">Services</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-sm text-gray-500">
                  Un email d'activation sera envoyé au client pour qu'il puisse activer son compte.
                </p>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={handleClose}>Annuler</Button>
                  <Button 
                    onClick={handleCreateNewClient} 
                    className="bg-[#1db584] hover:bg-[#18a075]"
                    disabled={creatingClient}
                  >
                    {creatingClient ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Création...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Créer et continuer
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* Étape 2: Formulaire d'opération (réutilisation de TransportForm) */}
        {step === 'operation' && selectedClient && !showSaveTemplatePrompt && (
          <div className="space-y-4">
            {/* Récapitulatif du client */}
            <Card className="bg-gray-50">
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1db584]/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#1db584]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{selectedClient.firstName} {selectedClient.lastName}</p>
                    <p className="text-sm text-gray-500">{selectedClient.email}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setStep('client')}>
                    Changer
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Template Selector */}
            {templates.length > 0 && (
              <div className="border rounded-lg" data-testid="template-selector">
                <button
                  type="button"
                  onClick={() => document.getElementById('template-panel')?.classList.toggle('hidden')}
                  className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors rounded-lg"
                  data-testid="template-toggle-btn"
                >
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span>Utiliser un template</span>
                    <Badge variant="secondary" className="text-xs">{templates.length}</Badge>
                  </div>
                  {selectedTemplate && (
                    <Badge className="bg-[#1db584] text-white text-xs">{selectedTemplate.name}</Badge>
                  )}
                </button>
                <div id="template-panel" className="border-t px-3 pb-3 space-y-2">
                  {loadingTemplates ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {templates.map((tpl) => (
                        <button
                          type="button"
                          key={tpl.id || tpl.name}
                          className={`text-left p-3 rounded-lg border transition-all ${
                            selectedTemplate?.id === tpl.id 
                              ? 'border-[#1db584] bg-green-50 ring-1 ring-[#1db584]' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedTemplate(tpl)}
                          data-testid={`template-card-${tpl.name}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="w-4 h-4 text-[#1db584] shrink-0" />
                            <span className="font-medium text-sm truncate">{tpl.name}</span>
                          </div>
                          <div className="space-y-0.5 text-xs text-gray-500">
                            {(tpl.departureAddress || tpl.arrivalAddress) && (
                              <div className="flex items-center gap-1">
                                <Route className="w-3 h-3 shrink-0" />
                                <span className="truncate">{tpl.departureAddress || '?'} → {tpl.arrivalAddress || '?'}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-3">
                              {tpl.vehicleType && (
                                <span className="flex items-center gap-1">
                                  <Truck className="w-3 h-3" />{tpl.vehicleType}
                                </span>
                              )}
                              {tpl.weight && (
                                <span className="flex items-center gap-1">
                                  <Weight className="w-3 h-3" />{tpl.weight}t
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedTemplate && (
                    <div className="flex justify-end pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs text-gray-500"
                        onClick={() => setSelectedTemplate(null)}
                        data-testid="clear-template-btn"
                      >
                        <X className="w-3 h-3 mr-1" /> Retirer le template
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Réutilisation du TransportForm existant */}
            <TransportForm 
              clientId={selectedClient.id}
              clientInfo={selectedClient}
              isAdminCreating={true}
              onSuccess={handleOperationCreated}
              onCancel={handleClose}
              initialData={selectedTemplate}
            />
          </div>
        )}

        {/* Étape 3: Sauvegarder comme template */}
        {showSaveTemplatePrompt && (
          <div className="space-y-5 py-2" data-testid="save-template-prompt">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3">
                <CheckCircle className="w-8 h-8 text-[#1db584]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Opération créée avec succès</h3>
              <p className="text-sm text-gray-500 mt-1">
                Souhaitez-vous sauvegarder cette configuration comme template ?
              </p>
            </div>

            <div>
              <Label className="text-sm">Nom du template</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Ex: Abidjan - San Pedro (Ciment)"
                className="mt-1"
                data-testid="template-name-input"
              />
              <p className="text-xs text-gray-500 mt-1">Ce template sera réutilisable pour vos prochaines opérations</p>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={handleSkipSaveTemplate} data-testid="skip-template-btn">
                Non merci
              </Button>
              <Button
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate || !templateName.trim()}
                className="bg-[#1db584] hover:bg-[#18a075]"
                data-testid="confirm-save-template-btn"
              >
                {savingTemplate ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sauvegarde...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Sauvegarder le template</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminCreateOperationModal;
